import { useEffect, useRef, useState } from 'react'
import { useT, useI18n } from '../../i18n/useT'
import { analyticsConsent, onOpenConsentSettings, saveAnalyticsConsent } from '../../lib/consent'
import { lingvanexUrl } from '../../lib/lingvanex'

/** GDPR consent dialog, visually mirroring the tarteaucitron banner on
 * app.lingvanex.com: centered white card over a dimmed page, stacked
 * full-width buttons, Accept and Reject with equal prominence. Rendered only
 * while no decision exists — neither our own record nor one inherited from
 * the main site's `.lingvanex.com` cookie (lib/consent.ts). Once decided, a
 * floating cookie icon (bottom-left, same as the main sites) re-opens the
 * dialog with the Customize panel expanded. */

const btnBase =
  'w-full rounded-lg px-5 py-3 text-[15px] font-medium transition-colors cursor-pointer ' +
  'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
const btnFilled = `${btnBase} bg-primary text-on-accent hover:bg-primary-hover`
const btnOutline = `${btnBase} border border-primary text-primary bg-transparent hover:bg-primary/5`

export function CookieConsentBanner() {
  const t = useT()
  const { locale } = useI18n()
  const [open, setOpen] = useState(() => analyticsConsent() === 'unset')
  const [customize, setCustomize] = useState(false)
  // Unchecked by default — a pre-ticked optional box is not valid consent
  const [analyticsChecked, setAnalyticsChecked] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  // No ✕ and no Esc on purpose (same as the main site): the dialog closes
  // only through an explicit choice — Accept, Reject or a saved Customize.
  useEffect(() => {
    if (open) cardRef.current?.focus()
  }, [open])

  // The footer's "Cookie settings" link — the way in on phones, where the
  // floating disc is hidden because it sits on top of the pricing cards
  useEffect(
    () =>
      onOpenConsentSettings(() => {
        setAnalyticsChecked(analyticsConsent() === 'granted')
        setCustomize(true)
        setOpen(true)
      }),
    [],
  )

  function decide(granted: boolean) {
    saveAnalyticsConsent(granted)
    setOpen(false)
    setCustomize(false)
  }

  if (!open) {
    // Decision exists → the tarteaucitron-style revisit icon, bottom-left
    // like on lingvanex.com/app.lingvanex.com. Desktop only: at phone width
    // it lands on the pricing grid's left column, and an opaque 42 px disc
    // over the card you are selling is a bad trade for a control people open
    // approximately never. The footer link covers that width instead.
    return (
      <button
        onClick={() => {
          setAnalyticsChecked(analyticsConsent() === 'granted')
          setCustomize(true)
          setOpen(true)
        }}
        aria-label={t('cookies.settings')}
        title={t('cookies.settings')}
        className="fixed bottom-5 left-5 z-40 hidden h-[42px] w-[42px] items-center justify-center rounded-full bg-primary shadow-md hover:bg-primary-hover transition-colors cursor-pointer sm:flex"
      >
        {/* The exact revisit icon the main sites use (copied from
            lingvanex.com's tarteaucitron assets), at their 30px size.
            Its white is baked into the file, and on dark the disc under it
            is the lighter blue — the same pair that made text-on-accent
            necessary. `invert` is what a token would do if an <img> could
            take one. */}
        <img src="/cookie-revisit.svg" alt="" width={30} height={30} className="dark:invert" />
      </button>
    )
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 dark:bg-black/70 flex items-center justify-center p-4">
      <div
        ref={cardRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={t('cookies.title')}
        className="relative w-full max-w-md bg-surface rounded-xl shadow-2xl p-6 sm:p-8 focus:outline-none"
      >
        {/* The consent sentence quotes the "Accept All" button — in the
            Customize view (e.g. opened via the revisit icon) that button
            isn't there, so a neutral line replaces it */}
        <p className="text-[15px] leading-relaxed text-text">
          {customize ? t('cookies.manageText') : t('cookies.text')}
        </p>

        {customize && (
          <div className="mt-5 space-y-3">
            <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
              <div>
                <p className="text-sm font-medium text-text">{t('cookies.necessary')}</p>
                <p className="text-xs text-text-secondary mt-0.5">{t('cookies.necessaryDesc')}</p>
              </div>
              <span className="text-xs font-medium text-text-muted whitespace-nowrap mt-0.5">
                {t('cookies.alwaysOn')}
              </span>
            </div>
            <label className="flex items-start justify-between gap-3 rounded-lg border border-border p-3 cursor-pointer hover:bg-surface-hover transition-colors">
              <div>
                <p className="text-sm font-medium text-text">{t('cookies.analytics')}</p>
                <p className="text-xs text-text-secondary mt-0.5">{t('cookies.analyticsDesc')}</p>
              </div>
              <input
                type="checkbox"
                checked={analyticsChecked}
                onChange={(e) => setAnalyticsChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-primary cursor-pointer"
              />
            </label>
            <a
              href={lingvanexUrl('/cookies-policy/', locale)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-xs text-text-muted hover:text-text underline"
            >
              {t('footer.cookies')}
            </a>
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3">
          {customize ? (
            <button onClick={() => decide(analyticsChecked)} className={btnFilled}>
              {t('cookies.save')}
            </button>
          ) : (
            <>
              <button onClick={() => decide(true)} className={btnFilled}>
                {t('cookies.acceptAll')}
              </button>
              <button onClick={() => decide(false)} className={btnFilled}>
                {t('cookies.rejectAll')}
              </button>
              <button onClick={() => setCustomize(true)} className={btnOutline}>
                {t('cookies.customize')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
